/**
 * Wie eine gemessene Tokenzahl beschriftet wird — und sonst nichts.
 *
 * ACHTUNG, was dieses Modul NICHT tut, und das ist sein ganzer Zuschnitt: es
 * rechnet nicht. Keine Schwelle, kein Verhältnis, kein Vergleich gegen
 * `num_ctx`. Jede Zahl kommt fertig aus der Antwort des Dienstes, der sie
 * gemessen hat. Sobald hier gerechnet würde, gäbe es eine zweite Stelle, an
 * der eine Tokenzahl entsteht — und die läuft zuverlässig auseinander.
 *
 * ACHTUNG, warum das ein DRITTES Modul ist und keine Erweiterung der beiden
 * vorhandenen. Die drei beantworten drei verschiedene Fragen, und sie tragen
 * deshalb drei Namen — dieselbe Regel, die `reconstructed_count` von
 * `display_count` trennt:
 *
 *   promptMass.js    ZÄHLT Zeichen eines Textes, der im Browser vorliegt.
 *   tokenMessung.js  ZEIGT Token, die ein Dienst gemessen hat.
 *
 * Ein gemeinsames Modul lüde dazu ein, die eine Zahl für die andere zu halten.
 *
 * ⚠️ ACHTUNG, am 20.08. korrigiert. Hier stand bis dahin `belegMasse.js`
 * als dritte Auskunft mit dem Zusatz, seine Zeichen-Schätzung „bleibt bestehen
 * und wird NICHT ersetzt“. Das ist seit dem 19.08. falsch: die Schätzung ist
 * mit dem Warnsatz gestrichen worden (Entscheidung Simon), und `belegMasse.js`
 * sagt das in seinem eigenen Kopf. Ein Kommentar, der eine gestrichene
 * Rechnung als lebend beschreibt, lässt die nächste Sitzung nach ihr suchen.
 */

/**
 * Eine gemessene Tokenzahl in Tausend — die einzige Rechnung dieses Moduls.
 *
 * ACHTUNG, das ist KEINE Ausnahme von „hier wird nicht gerechnet". Der
 * Grundsatz oben verbietet, eine Tokenzahl zu ERZEUGEN oder gegen eine
 * Schwelle zu verrechnen; er verbietet nicht, die vom Dienst gemessene Zahl
 * anders zu schreiben. Die Zahl bleibt dieselbe, nur ihre Auflösung ist
 * gröber — und das steht im Text als `≈` bzw. `~` dabei.
 *
 * Eine Nachkommastelle, Komma statt Punkt: 3387 → „3,4k", 856 → „0,9k",
 * 12040 → „12,0k". Unter 1000 wird ausdrücklich NICHT auf „0k" gerundet,
 * sondern auf „0,9k" — eine 0 wäre an dieser Stelle eine Falschaussage.
 */
function tausend(token) {
  return (token / 1000).toFixed(1).replace(".", ",") + "k";
}

/**
 * Was gemessen wurde, in einem Satz — oder "".
 *
 * Leer heißt: es liegt keine Messung vor. Nicht "0 Token" und nicht "noch
 * nicht gemessen" — das unterscheidet der Aufrufer, der weiß, ob er den Knopf
 * gedrückt hat.
 *
 * ACHTUNG, der Satz nannte bis zum 19.08. AUCH die Zeichenzahl, das Modell und
 * das Kontextfenster des Laufs. Er ist auf Entscheidung Simons auf zwei
 * Angaben gekürzt: die Tokenzahl und den Belegeanteil. Der Anlass ist die
 * Lesbarkeit für einen Fremden — „Fenster 16384" ist ohne die Geschichte von
 * OPTIMIZER_NUM_CTX nicht zu deuten und wurde für eine Eigenschaft des
 * Modells gehalten (qwen3.5:9b kann 262144).
 *
 * ⚠️ Der Preis ist benannt und nicht wegdiskutiert: die Tokenzahl ist
 * MODELLABHÄNGIG (verschiedene Tokenizer), und ohne den Modellnamen daneben
 * ist sie streng genommen unattribuiert. Getragen wird das davon, dass
 * unmittelbar daneben die Modellauswahl steht, aus der die Messung stammt —
 * und dass die Messung verfällt, sobald jemand das Modell wechselt (siehe
 * watch in ReflectionPage.vue). Wer diese Kopplung löst, holt den Modellnamen
 * zurück in den Satz.
 */
export function messsatz(messung) {
  const m = messung || {};
  const prompt = m.meta_prompt || null;
  if (!prompt || typeof prompt.token !== "number") return "";

  const teile = [];
  // „mindestens", wenn Ollama gekappt hat: die Zahl ist dann eine Untergrenze
  // und keine Messung. Wer das verschweigt, zeigt eine Zahl, die kleiner ist
  // als der Text. Das `≈` entfällt dann — „mindestens" sagt schon, in welche
  // Richtung die Zahl unsicher ist, und beides zusammen widerspräche sich.
  teile.push(
    `${prompt.fenster_voll === true ? "mindestens " : "≈"}` +
      `${tausend(prompt.token)} Token`
  );

  const belege = m.belege || null;
  if (belege && typeof belege.token === "number") {
    // „roher Text" steht dabei, weil dieser Teil ohne Rahmen gemessen ist —
    // sonst addiert jemand die beiden Zahlen und rechnet den Rahmen doppelt.
    //
    // ⚠️ Hier stand bis zum 24.08. „für die Belege". Das Wort meint auf dieser
    // Seite die kompletten Nachrichtentexte, die in den Meta-Prompt gehen —
    // und genau so heißt das Eingabefeld darüber seit dem 17.08. („Komplette
    // Nachrichten im Prompt"). Zwei Wörter für dieselbe Sache, auf derselben
    // Seite, sind die Vermischung, um die es geht. Der Bezeichner `belege`
    // bleibt: er kommt aus der Antwort des Dienstes.
    teile.push(
      `davon ~${tausend(belege.token)} für die Nachrichtentexte (roh gemessen)`
    );
  }

  return teile.join(" · ");
}

/**
 * Ist die Messung besorgniserregend — und warum?
 *
 * Liefert "" im Normalfall. Gewarnt wird bei genau einer Lage, und die ist
 * keine Schwelle, sondern eine Tatsache: der Dienst meldet, dass er das
 * Kontextfenster ausgereizt hat. Dann hat Ollama den Prompt bereits gekappt,
 * und was das Modell nicht gesehen hat, kann es nicht befolgen.
 *
 * ⚠️ Ausdrücklich KEINE eigene Schwelle bei 80 % oder Ähnlichem. Eine solche
 * Zahl wäre erfunden. Dieselbe Begründung wie in belegMasse.js.
 *
 * ⚠️ ZWEI verschiedene Kürzungen, und sie werden auseinandergehalten:
 * `mess_gekuerzt` sagt, dass die MESSUNG an ihre eigene Grenze stieß (die
 * Zahl ist dann falsch), `fenster_voll` sagt, dass der gemessene Prompt nicht
 * in das Fenster des LAUFS passt (die Zahl ist richtig, der Lauf wird
 * kürzen).
 *
 * ⚠️ DREI Zustände, nicht zwei. `fenster_voll` ist `null`, wenn gar kein
 * Fenster angefordert wurde — dann nimmt Ollama sein eigenes, und niemand
 * hier kennt es. Das ist der REGELFALL: ohne gesetztes `OPTIMIZER_NUM_CTX`
 * geht kein `num_ctx` hinaus. Bis zum 18.08. kam an dieser Stelle ein
 * `false` an, und die Warnung konnte damit nie angehen — sie sah aus wie ein
 * Schutz und war keiner. Jetzt sagt der zweite Satz, dass die Frage offen ist.
 */
export function messwarnung(messung) {
  const teile = [messung?.meta_prompt, messung?.belege].filter(Boolean);
  if (!teile.length) return "";
  // Zuerst der schwerere Fall: die MESSUNG selbst wurde gekürzt, die Zahl ist
  // dann eine Untergrenze. Live gemessen (18.08.): Ollama wirft die erste
  // Hälfte weg und behält `fenster/2 + 3` — ein gekürzter Prompt liefert also
  // eine KLEINERE Zahl, nicht eine größere, und sieht wie eine vollständige
  // Messung aus.
  const gekuerzt = teile.find((t) => t.mess_gekuerzt);
  if (gekuerzt) {
    return (
      `Die Messung selbst wurde gekürzt (Messfenster ${gekuerzt.mess_fenster} ` +
      "Token). Die Zahl ist eine Untergrenze — der Prompt ist größer, um " +
      "wieviel sagt sie nicht."
    );
  }
  if (teile.some((t) => t.fenster_voll === true)) {
    return (
      "Das Kontextfenster ist ausgereizt — Ollama schneidet den Prompt ab, " +
      "ohne es zu melden. Die Zahl ist deshalb eine Untergrenze."
    );
  }
  return "";
}

/**
 * Der Vorbehalt zur Messung — im Info-Icon, nicht in der Fläche.
 *
 * ⚠️ Getrennt von messwarnung() und mit Absicht ohne Warnzeichen: dies ist
 * der REGELFALL, und ein Alarm, der immer angeht, wird nach dem dritten Mal
 * nicht mehr gelesen. Die gemessene Zahl IST richtig; ob Ollama den Prompt
 * beim LAUF kappt, weiß hier niemand — und eine gekappte Messung sieht wie
 * eine vollständige aus.
 *
 * ⚠️ Seit dem 19.08. ist der Regelfall wieder der Normalzustand:
 * OPTIMIZER_NUM_CTX ist zurückgestellt (Entscheidung Simon, möglichst wenige
 * ENV-Eingriffe), also geht bei JEDEM Lauf keine Fenstergröße hinaus. Damit
 * stünde dieser Satz dauerhaft unter der Zahl — genau das, wogegen am selben
 * Tag der Belegwarnsatz gefallen ist. Er steht deshalb im Info-Icon neben der
 * Zahl, nach dem Muster der Cutoff-Auskunft vom 18.08.
 *
 * ⚠️ Und er nennt die ENV NICHT mehr beim Namen. Der Satz wird von jemandem
 * gelesen, der die Anwendung benutzt und keine .env kennt; „OPTIMIZER_NUM_CTX
 * ist nicht gesetzt" ist für den keine Auskunft, sondern eine Fehlermeldung.
 * Wo die Variable steht und was sie tut, sagt docs/CONFIGURATION.md.
 */
export function messvorbehalt(messung) {
  const teile = [messung?.meta_prompt, messung?.belege].filter(Boolean);
  if (!teile.length) return "";
  if (teile.some((t) => t.fenster_voll === true)) return "";
  if (!teile.every((t) => t.fenster_voll === null || t.fenster_voll === undefined)) {
    return "";
  }
  return (
    "Die Zahl ist gemessen. Ob der Prompt beim Lauf vollständig ins " +
    "Kontextfenster passt, sagt sie nicht: der Lauf gibt kein Fenster vor, " +
    "und welches Ollama dann wählt, meldet es nicht."
  );
}

/**
 * Der Hilfetext am Messknopf.
 *
 * Er sagt die drei Dinge, die jemand vor dem Drücken wissen muss: dass es eine
 * Messung ist (keine Schätzung), was sie kostet, und dass sie folgenlos ist.
 */
export const HILFE_MESSEN =
  "Fährt einen echten Aufruf gegen das gewählte Modell und liest die " +
  "Tokenzahl des Prompts ab — warm rund eine Sekunde, nach einem Neustart " +
  "des Modells deutlich länger. Es entsteht kein Vorschlag und nichts wird " +
  "gespeichert.";
